(() => {
  // Animated logo groups: one section-wide clock rolls every group in a single
  // wave — lane N flips `stagger` after lane N-1 — then the whole section
  // rests until the next interval.
  const DEFAULT_INTERVAL_S = 5;
  const DEFAULT_STAGGER_MS = 100;
  const TRANSITION_MS = 650; // keep in sync with --logo-flip-duration

  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const setItemAccess = (item, active) => {
    item.toggleAttribute("inert", !active);
    item.setAttribute("aria-hidden", active ? "false" : "true");
  };

  /* ── Animated logo groups ── */

  const createGroupsController = (section) => {
    if (!section.querySelector(".js-logo-animated-group")) return null;

    const root = section.querySelector(".logo-list");
    const marqueeEl = section.querySelector("marquee-ticker");
    const eventController = new AbortController();
    // lane -> item index, so marquee copies of a group are seeded in step
    const laneIndex = new Map();
    const waveTimers = new Set();
    let entries = [];
    let cycleEntries = [];
    let byGroup = new Map();
    let staggerMs = 0;
    let observer = null;
    let marqueeObserver = null;
    let rebuildTimer = null;
    let onScreen = false;
    let hoverPaused = false;
    let focusPaused = false;
    let waveInterval = null;

    const intervalMs =
      (Number(root?.dataset.rotationInterval) || DEFAULT_INTERVAL_S) * 1000;
    const staggerSetting = Number(root?.dataset.rotationStagger);
    const requestedStaggerMs = Number.isFinite(staggerSetting)
      ? staggerSetting
      : DEFAULT_STAGGER_MS;
    const pauseOnHover = root?.dataset.rotationPauseOnHover === "true";
    // the marquee repeats the block set; the lane is the group's slot within one
    // repetition, so duplicated copies of a group share a flip moment
    const groupCount = Number(root?.dataset.rotationGroupCount) || 0;

    const build = () => {
      entries.forEach((entry) => {
        if (entry.leaveTimer) clearTimeout(entry.leaveTimer);
      });

      const groupEls = section.querySelectorAll(".js-logo-animated-group");
      const lanes = groupCount || groupEls.length;

      entries = [];
      byGroup = new Map();

      groupEls.forEach((groupEl, index) => {
        const items = Array.from(
          groupEl.querySelectorAll(
            ".logo-animated-group__viewport > .logo-card"
          )
        );
        if (items.length === 0) return;

        const lane = index % lanes;
        const current = (laneIndex.get(lane) || 0) % items.length;

        items.forEach((item, i) => {
          item.classList.remove("is-leaving");
          item.classList.toggle("is-active", i === current);
          setItemAccess(item, i === current);
        });

        groupEl.classList.add("logo-animated-group--initialized");

        const entry = { groupEl, items, lane, current, leaveTimer: null };
        entries.push(entry);
        byGroup.set(groupEl, entry);
      });

      cycleEntries = entries.filter((entry) => entry.items.length > 1);

      // Keep the whole wave (last flip + its transition) inside one interval so
      // it never runs into the next one.
      const lastLane = cycleEntries.reduce(
        (max, entry) => Math.max(max, entry.lane),
        0
      );
      const maxStaggerMs =
        lastLane > 0 ? Math.max(0, (intervalMs - TRANSITION_MS) / lastLane) : 0;
      staggerMs = Math.min(requestedStaggerMs, maxStaggerMs);
    };

    build();

    const advance = (entry) => {
      const prev = entry.items[entry.current];
      // swapping out a focused logo would make it inert and drop focus to <body>
      if (prev.contains(document.activeElement)) return;
      entry.current = (entry.current + 1) % entry.items.length;
      laneIndex.set(entry.lane, entry.current);
      const next = entry.items[entry.current];

      prev.classList.remove("is-active");
      prev.classList.add("is-leaving");
      next.classList.add("is-active");
      setItemAccess(prev, false);
      setItemAccess(next, true);

      if (entry.leaveTimer) clearTimeout(entry.leaveTimer);
      entry.leaveTimer = setTimeout(() => {
        prev.classList.remove("is-leaving");
        entry.leaveTimer = null;
      }, TRANSITION_MS);
    };

    const runWave = () => {
      cycleEntries.forEach((entry) => {
        const offset = entry.lane * staggerMs;
        if (offset === 0) {
          advance(entry);
          return;
        }

        const timer = setTimeout(() => {
          waveTimers.delete(timer);
          advance(entry);
        }, offset);
        waveTimers.add(timer);
      });
    };

    const start = () => {
      if (waveInterval || cycleEntries.length === 0) return;
      if (prefersReducedMotion() || hoverPaused || focusPaused || !onScreen) {
        return;
      }

      waveInterval = setInterval(runWave, intervalMs);
    };

    const stop = () => {
      if (waveInterval) {
        clearInterval(waveInterval);
        waveInterval = null;
      }
      waveTimers.forEach((timer) => clearTimeout(timer));
      waveTimers.clear();
    };

    const showItem = (groupEl, itemEl) => {
      const entry = byGroup.get(groupEl);
      if (!entry) return;

      const index = entry.items.indexOf(itemEl);
      if (index === -1) return;

      if (entry.leaveTimer) {
        clearTimeout(entry.leaveTimer);
        entry.leaveTimer = null;
      }

      entry.items.forEach((item, i) => {
        item.classList.remove("is-leaving");
        item.classList.toggle("is-active", i === index);
        setItemAccess(item, i === index);
      });
      entry.current = index;
      laneIndex.set(entry.lane, index);
    };

    // the marquee clones its content once fonts settle and again on resize —
    // rebuild so every copy joins the wave in its lane's state
    const scheduleRebuild = () => {
      if (rebuildTimer) clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(() => {
        rebuildTimer = null;
        stop();
        build();
        start();
      }, 50);
    };

    const destroy = () => {
      stop();
      eventController.abort();
      if (rebuildTimer) {
        clearTimeout(rebuildTimer);
        rebuildTimer = null;
      }
      entries.forEach((entry) => {
        if (entry.leaveTimer) {
          clearTimeout(entry.leaveTimer);
          entry.leaveTimer = null;
        }
      });
      if (marqueeObserver) {
        marqueeObserver.disconnect();
        marqueeObserver = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };

    if (entries.length > 0) {
      observer = new IntersectionObserver(
        (obsEntries) => {
          if (obsEntries[0]?.isIntersecting) {
            onScreen = true;
            start();
          } else {
            onScreen = false;
            stop();
          }
        },
        { threshold: 0 }
      );
      observer.observe(section);

      if (marqueeEl) {
        marqueeObserver = new MutationObserver(scheduleRebuild);
        marqueeObserver.observe(marqueeEl, { childList: true, subtree: true });
      }

      const hoverEl = section.querySelector(".logo-list__group");

      // Keyboard focus always holds the wave, whatever the hover setting says.
      if (hoverEl) {
        hoverEl.addEventListener(
          "focusin",
          () => {
            focusPaused = true;
            stop();
          },
          { signal: eventController.signal }
        );
        hoverEl.addEventListener(
          "focusout",
          (event) => {
            if (hoverEl.contains(event.relatedTarget)) return;
            focusPaused = false;
            start();
          },
          { signal: eventController.signal }
        );
      }

      if (pauseOnHover && hoverEl) {
        hoverEl.addEventListener(
          "mouseenter",
          () => {
            hoverPaused = true;
            stop();
          },
          { signal: eventController.signal }
        );
        hoverEl.addEventListener(
          "mouseleave",
          () => {
            hoverPaused = false;
            start();
          },
          { signal: eventController.signal }
        );
      }
    }

    return { start, stop, showItem, destroy };
  };

  /* ── Section lifecycle ── */

  const destroySection = (section) => {
    const state = section?._logoListState;
    if (!state) return;

    if (state.groups) state.groups.destroy();

    section._logoListState = null;
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains("logo-list-section")) return;

    destroySection(section);

    section._logoListState = { groups: createGroupsController(section) };
  };

  initSection(document.currentScript.parentElement);

  if (!window.__themeLogoListEvents) {
    window.__themeLogoListEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:reorder", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      destroySection(event.target);
    });

    document.addEventListener("shopify:block:select", (event) => {
      const section = event.target.closest(".logo-list-section");
      const state = section?._logoListState;
      if (!state?.groups) return;

      state.groups.stop();

      const groupEl = event.target.closest(".js-logo-animated-group");
      if (groupEl && event.target.classList.contains("logo-card")) {
        state.groups.showItem(groupEl, event.target);
      }
    });

    document.addEventListener("shopify:block:deselect", (event) => {
      const section = event.target.closest(".logo-list-section");
      section?._logoListState?.groups?.start();
    });
  }
})();
